import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Paper, Stack, TextField, Typography, Grid, MenuItem,
  FormControlLabel, Checkbox, Divider, IconButton, Alert, Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import {
  obterModelo, salvarModelo, uploadArteModelo, removerArteModelo, arteModeloUrl, previaModelo,
} from "../../services/certificadoService";
import { INSTITUICOES } from "../../config/instituicoes";

const vazio = {
  Id: 0, Nome: "", Instituicao: "", Cidade: "Araguaína - TO", Texto: "",
  Assinante1: "", Cargo1: "", Assinante2: "", Cargo2: "", Ativo: true,
  TextoHistorico: "", PadraoHistorico: false,
};

const ARTES = [
  { tipo: "fundo", rotulo: "Arte de fundo", dica: "A4 paisagem (ex.: 3508×2480 px), sem os textos — eles são impressos por cima." },
  { tipo: "assinatura1", rotulo: "Assinatura 1", dica: "PNG com fundo transparente." },
  { tipo: "assinatura2", rotulo: "Assinatura 2", dica: "PNG com fundo transparente." },
];

export default function CertificadoModeloForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(vazio);
  const [artes, setArtes] = useState({ fundo: false, assinatura1: false, assinatura2: false });
  const [cacheBuster, setCacheBuster] = useState(0);
  const [textoHistPadrao, setTextoHistPadrao] = useState("");
  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await obterModelo(id);
        setM({
          Id: data.Id, Nome: data.Nome || "", Instituicao: data.Instituicao || "", Cidade: data.Cidade || "",
          Texto: data.Texto || "", Assinante1: data.Assinante1 || "", Cargo1: data.Cargo1 || "",
          Assinante2: data.Assinante2 || "", Cargo2: data.Cargo2 || "", Ativo: !!data.Ativo,
          TextoHistorico: data.TextoHistorico || "", PadraoHistorico: !!data.PadraoHistorico,
        });
        setTextoHistPadrao(data.TextoHistoricoPadrao || "");
        setArtes({ fundo: !!data.TemFundo, assinatura1: !!data.TemAssinatura1, assinatura2: !!data.TemAssinatura2 });
      } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    })();
  }, [id]);

  const salvar = async () => {
    if (!m.Nome.trim()) return Swal.fire("Atenção", "Informe o nome do modelo.", "warning");
    try {
      const { data } = await salvarModelo(m);
      await Swal.fire("Sucesso", data.mensagem, "success");
      if (!id) navigate(`/certificados/${data.id}`); else set("Id", data.id);
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const enviarArte = async (tipo, file) => {
    if (!file) return;
    try {
      await uploadArteModelo(m.Id, tipo, file);
      setArtes((a) => ({ ...a, [tipo]: true }));
      setCacheBuster(Date.now());
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };
  const removerArte = async (tipo) => {
    try {
      await removerArteModelo(m.Id, tipo);
      setArtes((a) => ({ ...a, [tipo]: false }));
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const verPrevia = async () => {
    try { await previaModelo(m.Id, m.Texto || undefined); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{id ? "Editar modelo de certificado" : "Novo modelo de certificado"}</Typography>
        <Stack direction="row" spacing={1}>
          {m.Id > 0 && (
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={verPrevia}>Pré-visualizar</Button>
          )}
          <Button onClick={() => navigate("/certificados")}>Voltar</Button>
          <Button variant="contained" onClick={salvar}>Salvar</Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField fullWidth label="Nome do modelo (ex.: NEIC — Faculdade Católica)" value={m.Nome}
              onChange={(e) => set("Nome", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField select fullWidth label="Instituição (informativo)" value={m.Instituicao}
              onChange={(e) => set("Instituicao", e.target.value)}>
              <MenuItem value="">— não definir —</MenuItem>
              {Object.values(INSTITUICOES).map((i) => <MenuItem key={i.id} value={i.id}>{i.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 8, sm: 2 }}>
            <TextField fullWidth label="Cidade" value={m.Cidade} onChange={(e) => set("Cidade", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 4, sm: 1 }} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel control={<Checkbox checked={m.Ativo} onChange={(e) => set("Ativo", e.target.checked)} />} label="Ativo" />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField fullWidth multiline minRows={3} label="Texto padrão (o evento pode ter o seu)" value={m.Texto}
              onChange={(e) => set("Texto", e.target.value)}
              helperText={<>Campos: <code>{"{nome}"}</code> <code>{"{evento}"}</code> <code>{"{participacao}"}</code> <code>{"{carga}"}</code> <code>{"{periodo}"}</code> <code>{"{oficinas}"}</code> <code>{"{cidade}"}</code> <code>{"{data}"}</code> <code>{"{numero}"}</code></>} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" color="primary">Assinatura 1</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            <TextField fullWidth label="Nome" value={m.Assinante1} onChange={(e) => set("Assinante1", e.target.value)} sx={{ mb: 1 }} />
            <TextField fullWidth label="Cargo" value={m.Cargo1} onChange={(e) => set("Cargo1", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" color="primary">Assinatura 2</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            <TextField fullWidth label="Nome" value={m.Assinante2} onChange={(e) => set("Assinante2", e.target.value)} sx={{ mb: 1 }} />
            <TextField fullWidth label="Cargo" value={m.Cargo2} onChange={(e) => set("Cargo2", e.target.value)} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Eventos do sistema anterior</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            <FormControlLabel
              control={<Checkbox checked={m.PadraoHistorico} onChange={(e) => set("PadraoHistorico", e.target.checked)} />}
              label="Usar este modelo nos certificados de eventos antigos (histórico)" />
            <TextField fullWidth multiline minRows={2} label="Texto do certificado do histórico" value={m.TextoHistorico}
              onChange={(e) => set("TextoHistorico", e.target.value)}
              placeholder={textoHistPadrao}
              helperText={<>Vazio usa o texto padrão. Aqui valem só <code>{"{nome}"}</code> e <code>{"{texto}"}</code> — o <code>{"{texto}"}</code> é a frase que o sistema antigo já guarda para cada evento (nome do evento, datas e carga horária).</>} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Imagens</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            {m.Id === 0 ? (
              <Alert severity="info">Salve o modelo para enviar a arte de fundo e as assinaturas.</Alert>
            ) : (
              <Grid container spacing={2}>
                {ARTES.map((a) => (
                  <Grid key={a.tipo} size={{ xs: 12, sm: 4 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.rotulo}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{a.dica}</Typography>
                      {artes[a.tipo] && (
                        <Box component="img" src={`${arteModeloUrl(m.Id, a.tipo)}?v=${cacheBuster}`} alt={a.rotulo}
                          sx={{ width: "100%", maxHeight: 130, objectFit: "contain", border: "1px solid #eee", borderRadius: 1, mb: 1, bgcolor: "#fafafa" }} />
                      )}
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Button component="label" size="small" variant={artes[a.tipo] ? "text" : "outlined"} startIcon={<UploadFileIcon />}>
                          {artes[a.tipo] ? "Trocar" : "Enviar"}
                          <input type="file" hidden accept="image/*"
                            onChange={(ev) => { enviarArte(a.tipo, ev.target.files?.[0]); ev.target.value = ""; }} />
                        </Button>
                        {artes[a.tipo] && (
                          <Tooltip title="Remover"><IconButton size="small" color="error" onClick={() => removerArte(a.tipo)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {m.Id > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" action={<Button size="small" startIcon={<VisibilityIcon />} onClick={verPrevia}>Pré-visualizar</Button>}>
                A prévia abre um certificado de exemplo (nome e evento fictícios) com esta arte — confira se os textos
                ficam em cima da área livre da imagem.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
}
